import * as React from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';

import { cn } from '../../utils/cn';

export const popoverContentVariants = cva(
    'origin-(--radix-popover-content-transform-origin) data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 outline-hidden z-popover w-fit rounded-2xl text-black shadow-lg',
    {
        variants: {
            variant: {
                default:
                    'before:backdrop-hack before:rounded-2xl before:backdrop-blur-2xl',
                shadcn: 'w-72 rounded-md bg-popover',
                glassy: 'before:backdrop-hack before:bg-gray-100/50 before:rounded-2xl before:backdrop-blur-island',
                white: 'before:rounded-xl before:bg-white',
            },
            paddings: {
                default: 'px-7 py-6',
                small: 'px-3 py-5',
                xsmall: 'px-2 py-2',
                vertical: 'py-6',
                verticalSmall: 'py-4',
                noPadding: 'p-0',
            },
            background: {
                default: 'before:bg-gray-100/85',
                none: '',
                transparent: 'before:bg-transparent',
                white: 'bg-white before:bg-white',
            },
        },
        defaultVariants: {
            variant: 'default',
            paddings: 'default',
            background: 'default',
        },
    },
);

export const PopoverContent = React.forwardRef<
    React.ElementRef<typeof PopoverPrimitive.Content>,
    React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content> &
        VariantProps<typeof popoverContentVariants> & {
            usePortal?: boolean;
        }
>(
    (
        {
            className,
            align = 'center',
            sideOffset = 4,
            variant,
            paddings,
            background,
            usePortal = true,
            ...props
        },
        ref,
    ) => {
        const content = (
            <PopoverPrimitive.Content
                ref={ref}
                data-slot="popover-content"
                align={align}
                sideOffset={sideOffset}
                className={cn(
                    popoverContentVariants({
                        variant,
                        paddings,
                        background,
                    }),
                    className,
                )}
                {...props}
            />
        );

        return usePortal ? (
            <PopoverPrimitive.Portal>{content}</PopoverPrimitive.Portal>
        ) : (
            content
        );
    },
);

PopoverContent.displayName = PopoverPrimitive.Content.displayName;
